import {
    isActiveStatus,
    StartVirtualCandidateSessionRequest,
    VirtualCandidateProgress,
    VirtualCandidateService,
    VirtualCandidateSession,
    VirtualCandidateSpeaker,
    VirtualCandidateUtterance,
} from 'src/services/backend/VirtualCandidateService'
import { LiveAudioPlayer } from 'src/services/virtual-candidate/LiveAudioPlayer'

/** Whether the live audio is playing. */
export type VirtualCandidateAudio = 'playing' | 'off' | 'failed'

/** idle → starting → live → ended, or error from starting or live. */
export type VirtualCandidatePhase = 'idle' | 'starting' | 'live' | 'ended' | 'error'

export interface VirtualCandidateState {
    phase: VirtualCandidatePhase
    session?: VirtualCandidateSession
    utterances: VirtualCandidateUtterance[]
    /** Who is audible right now, as of the last refresh. */
    speaking: VirtualCandidateSpeaker | null
    /** How far the interview has got, as of the last refresh; null before it starts and after it ends. */
    progress: VirtualCandidateProgress | null
    /** Live audio levels for the visualizer while the stream plays. */
    analyser?: AnalyserNode
    /** An end-interview request is in flight or accepted; Irene is closing. */
    stopping: boolean
    /** The live audio: playing, stopped by the user (or not started), or dropped by an error. */
    audio: VirtualCandidateAudio
    error?: string
}

export const INITIAL_STATE: VirtualCandidateState = Object.freeze({
    phase: 'idle',
    utterances: [],
    speaking: null,
    progress: null,
    stopping: false,
    audio: 'off',
}) as VirtualCandidateState

/** How often status, transcript and the active speaker refresh while the interview runs. */
export const POLL_INTERVAL_MS = 1000

const messageOf = (error: unknown): string => {
    const response = (error as { response?: { data?: { message?: string } } }).response
    return response?.data?.message ?? (error instanceof Error ? error.message : String(error))
}

/**
 * One virtual candidate run from Designer: starts a session, plays its live audio, and follows
 * its status and transcript until it ends. Each start() or close() begins a new generation, so
 * answers that arrive for an earlier run are ignored.
 */
export class VirtualCandidateSessionController {
    private state: VirtualCandidateState = INITIAL_STATE
    private player: LiveAudioPlayer | undefined
    private pollTimer: ReturnType<typeof setTimeout> | undefined
    private generation = 0
    /** The message shown because End interview failed; a later successful refresh clears it. */
    private stopError: string | undefined
    /** The message shown because the live audio dropped; reconnecting or disconnecting clears it. */
    private audioError: string | undefined
    private readonly onChange: (state: VirtualCandidateState) => void
    private readonly createAudioContext: () => AudioContext

    // Explicit fields: this package's Babel build drops TypeScript constructor parameter properties.
    constructor(
        onChange: (state: VirtualCandidateState) => void,
        createAudioContext: () => AudioContext = () => new AudioContext()
    ) {
        this.onChange = onChange
        this.createAudioContext = createAudioContext
    }

    get current(): VirtualCandidateState {
        return this.state
    }

    /**
     * Call from a click handler: the audio context is created before anything is awaited. The
     * request is built here too, since it needs calls of its own; their errors fail the run.
     */
    start(buildRequest: () => Promise<StartVirtualCandidateSessionRequest>): void {
        this.reset()
        const generation = this.generation
        const player = new LiveAudioPlayer(this.createAudioContext())
        this.player = player
        this.update({
            ...INITIAL_STATE,
            phase: 'starting',
            analyser: player.analyser,
            audio: 'playing',
        })
        void this.run(generation, player, buildRequest)
    }

    /**
     * Call from a click handler: listens to the running interview again with a fresh audio
     * context, from what is being said now. The interview itself never stopped.
     */
    reconnectAudio(): void {
        const { session } = this.state
        if (!session || !isActiveStatus(session.status) || this.state.phase !== 'live') return
        this.player?.stop()
        const player = new LiveAudioPlayer(this.createAudioContext())
        this.player = player
        this.update({
            ...this.state,
            analyser: player.analyser,
            audio: 'playing',
            error: this.withoutAudioError(),
        })
        void this.listen(this.generation, player, session.sessionId)
    }

    /** Stops the live audio only; the interview keeps running and the transcript keeps updating. */
    disconnectAudio(): void {
        this.player?.stop()
        this.player = undefined
        this.update({
            ...this.state,
            analyser: undefined,
            audio: 'off',
            error: this.withoutAudioError(),
        })
    }

    /** Asks Irene to close the interview; the audio continues until she has. */
    async endInterview(): Promise<void> {
        const { session } = this.state
        if (!session || !isActiveStatus(session.status) || this.state.stopping) return
        const generation = this.generation
        this.update({ ...this.state, stopping: true })
        try {
            await VirtualCandidateService.stopSession(session.sessionId)
        } catch (error) {
            if (generation === this.generation) {
                this.stopError = messageOf(error)
                this.update({ ...this.state, stopping: false, error: this.stopError })
            }
        }
    }

    /** Stops listening; a running interview is ended too, so it doesn't run unheard. */
    close(): void {
        const { session } = this.state
        if (session && isActiveStatus(session.status)) {
            void VirtualCandidateService.stopSession(session.sessionId).catch(() => undefined)
        }
        this.reset()
        this.update(INITIAL_STATE)
    }

    private async run(
        generation: number,
        player: LiveAudioPlayer,
        buildRequest: () => Promise<StartVirtualCandidateSessionRequest>
    ): Promise<void> {
        let session: VirtualCandidateSession
        try {
            const request = await buildRequest()
            if (generation !== this.generation) return
            session = await VirtualCandidateService.startSession(request)
        } catch (error) {
            if (generation === this.generation) this.fail(messageOf(error))
            return
        }
        if (generation !== this.generation) {
            // Closed while the session was starting: don't leave it running.
            void VirtualCandidateService.stopSession(session.sessionId).catch(() => undefined)
            return
        }
        this.update({ ...this.state, phase: 'live', session })
        void this.listen(generation, player, session.sessionId)
        await this.poll(generation, session.sessionId)
    }

    /** Plays the session's live audio on player; a failure marks the audio as dropped. */
    private async listen(
        generation: number,
        player: LiveAudioPlayer,
        sessionId: string
    ): Promise<void> {
        // A reconnect or disconnect replaced this player, or the run ended: ignore its outcome.
        const current = () => generation === this.generation && this.player === player
        try {
            const idToken = await VirtualCandidateService.getIdToken()
            await player.play(VirtualCandidateService.listenUrl(sessionId), idToken)
        } catch (error) {
            if (!current()) return
            this.audioError = messageOf(error)
            this.update({ ...this.state, audio: 'failed', error: this.audioError })
        }
    }

    /** The current error, unless it is the live audio's, which reconnecting replaces. */
    private withoutAudioError(): string | undefined {
        const { error } = this.state
        const cleared = this.audioError !== undefined && error === this.audioError
        this.audioError = undefined
        return cleared ? undefined : error
    }

    private async poll(generation: number, sessionId: string): Promise<void> {
        try {
            const [session, transcript] = await Promise.all([
                VirtualCandidateService.getSession(sessionId),
                VirtualCandidateService.getTranscript(sessionId),
            ])
            if (generation !== this.generation) return
            const ended = !isActiveStatus(session.status)
            this.update({
                ...this.state,
                session,
                utterances: transcript.utterances,
                speaking: ended ? null : transcript.speaking,
                progress: ended ? null : transcript.progress,
                phase: ended ? 'ended' : 'live',
                stopping: ended ? false : this.state.stopping,
                // An End interview failure is retryable, so a refresh that works clears it. Live
                // audio and token errors stay: refreshes keep working while the audio is broken.
                error:
                    this.stopError !== undefined && this.state.error === this.stopError
                        ? undefined
                        : this.state.error,
            })
            this.stopError = undefined
            if (ended) return
        } catch {
            // A missed refresh is retried on the next tick.
            if (generation !== this.generation) return
        }
        this.pollTimer = setTimeout(() => void this.poll(generation, sessionId), POLL_INTERVAL_MS)
    }

    private fail(error: string): void {
        this.player?.stop()
        this.player = undefined
        this.update({ ...this.state, phase: 'error', analyser: undefined, error })
    }

    private reset(): void {
        this.generation++
        this.stopError = undefined
        this.audioError = undefined
        clearTimeout(this.pollTimer)
        this.pollTimer = undefined
        this.player?.stop()
        this.player = undefined
    }

    private update(next: VirtualCandidateState): void {
        this.state = next
        this.onChange(next)
    }
}
